/**
 * Navigator for links the assistant writes into its answers.
 *
 * Deliberately built only on `useNavigate` + `useLocation` — NOT on `openPanel`
 * from `useSettingsNavigation`: that clears `?tab=` for every target except
 * `processManagement` and fires two network requests on mount, so it must never
 * appear in the message renderer at all. `useOutletContext` is ruled out too,
 * because the question panel is a sibling of the `<Outlet>`.
 *
 * `parseInternalHref` is also the *only* place that decides whether an address
 * is portal-internal. `markdownLinkComponent.tsx` shares it via
 * `isInternalGuideHref()` — two separate checks would drift apart, and the
 * weaker of the two would be the one that counts.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from 'i18next';

import { MOBILE_BREAKPOINT } from '../hooks/use-mobile';
import { foldAddress, foldPathname, foldSearch } from './address';
import { readMorphEnvironment, requestChatHandoff, shouldMorphNow } from './chatHandoff';
import { beginAssistantMorph } from './morph/assistantMorph';
import { measureAssistantSurface } from './morph/assistantSurface';
import { requestSpotlight } from './spotlight';
import type { PortalFeature } from './types';

/** 'navigated' = jumped, 'same' = already there, 'rejected' = address unusable. */
export type GuideNavigateResult = 'navigated' | 'same' | 'rejected';

/** Areas where a query-only change should not bloat the history stack. */
const QUERY_ONLY_ROOTS = ['/settings', '/administration'];

/** Root of the main chat — starting point of the handoff to the question panel. */
const CHAT_ROOT = '/chatbot';

/** Time window in which the question panel recognizes a navigation as "self-triggered". */
export const SELF_NAVIGATION_WINDOW_MS = 2000;

/**
 * Module-wide marker instead of a boolean one-shot flag: React batches the
 * navigation's state updates, so a flag would be overtaken by the reset effect.
 *
 * The marker also carries its **target**. A bare timestamp would also report
 * the next *foreign* navigation within the window as "self-triggered" — e.g.
 * when the user clicks in the sidebar a second after a guide query jump.
 * Reading deliberately does **not** consume the marker: under
 * `<React.StrictMode>` effects run twice in development, so a one-shot flag
 * would already be spent on the second run. Binding to the target achieves the
 * same distinction idempotently.
 */
let selfNavigation: { until: number; target: string } | null = null;

/**
 * Set before every own `navigate` — the question panel reads it in its reset
 * effect. `target` is the jump target as `pathname + search`.
 */
export function markSelfNavigation(target: string): void {
    selfNavigation = { until: Date.now() + SELF_NAVIGATION_WINDOW_MS, target: foldAddress(target) };
}

/**
 * True while the last own jump is within the time window **and** `current` is
 * exactly its target (`pathname + search`, as `useLocation` returns it).
 */
export function isSelfNavigation(current: string): boolean {
    if (!selfNavigation) return false;
    if (Date.now() >= selfNavigation.until) return false;
    return foldAddress(current) === selfNavigation.target;
}

/** Tests only: hard-reset the marker. */
export function resetSelfNavigation(): void {
    selfNavigation = null;
}

/**
 * What gets pointed at on an address when several entries carry it: **the
 * control, not the place.** Ten of the addresses in the registry carry both the
 * area itself (`nav.alerts`) and its create button (`settings.alerts.add`).
 * Pointing at the area while the user is already in it would be the most
 * useless answer of all.
 */
const SPOTLIGHT_KIND_RANK: Record<string, number> = {
    action: 0,
    dialog: 0,
    menu: 0,
    tab: 1,
    panel: 2,
    page: 2,
};

/**
 * Entries whose `route` is only a placeholder. The header is on *all* shell
 * pages, but the schema requires exactly one address — so
 * `shell.header.assistantPanel` carries `/settings`. Without this boundary a
 * click on `[Einstellungen öffnen](/settings)` would point at the header's
 * „Fragen" button: the only entry with a `guideId` that carries this address.
 */
const SPOTLIGHT_PLACEHOLDER_PREFIX = 'shell.';

/**
 * The registry entry for an address — but only if it has a target to point at
 * (`guideId`, package B4). The **folded** address is compared (`foldAddress`):
 * case and parameter order are the same view for react-router, and the
 * assistant writes its links from the registry while the user is on whatever
 * is in the address bar.
 *
 * Only the **bucket for the path** is compared: `featuresByRoute()` is keyed by
 * `pathname`, and address equality implies path equality. The keys are compared
 * folded rather than looked up, because the map holds them raw (`/settings`)
 * and the user may have `/Settings` in the address bar.
 *
 * Deliberately uncached: this is only called when a guided link points at the
 * page the user is already on. A cache, on the other hand, would have to know
 * when the registry changes (in tests: when a mock changes it).
 */
export function spotlightTargetFor(
    byRoute: Map<string, PortalFeature[]>,
    address: string,
): PortalFeature | undefined {
    const folded = foldAddress(address);
    const queryStart = address.indexOf('?');
    const foldedPath = foldPathname(queryStart >= 0 ? address.slice(0, queryStart) : address);
    let hit: PortalFeature | undefined;
    let rank = Number.POSITIVE_INFINITY;
    byRoute.forEach((features, routePath) => {
        if (foldPathname(routePath) !== foldedPath) return;
        features.forEach((feature) => {
            if (!feature.guideId) return;
            if (feature.id.indexOf(SPOTLIGHT_PLACEHOLDER_PREFIX) === 0) return;
            if (foldAddress(feature.route) !== folded) return;
            const candidate = SPOTLIGHT_KIND_RANK[feature.kind] ?? 3;
            // A tie keeps the first: the registry is sorted by `id`, so the
            // choice is stable and does not depend on file order.
            if (candidate >= rank) return;
            rank = candidate;
            hit = feature;
        });
    });
    return hit;
}

/**
 * Requests the spotlight when the address hits an entry with a target.
 *
 * The registry is **lazy-loaded** for this. 230 entries with names, synonyms,
 * steps, prerequisites and evidence come to about 170 kB gzip — they sit in the
 * portal only for exactly this one lookup (everything else arrives ready-made
 * from the tool). Statically imported, they would end up in the main bundle via
 * `markdownLinkComponent` and add a sixth of a megabyte to every sign-in;
 * measured against the bundle before this change it was +168 kB.
 *
 * The jump itself stays synchronous — only the spotlight is lazy-loaded, and it
 * searches for its target over one and a half seconds anyway. If loading fails
 * (offline, chunk gone after a deploy), nothing happens: the route is in the
 * answer text.
 */
function requestSpotlightForAddress(address: string): void {
    void import('./featureRegistry')
        .then(({ featuresByRoute }) => {
            const feature = spotlightTargetFor(featuresByRoute(), address);
            if (!feature?.guideId) return;
            const locale = currentLocale();
            // **No `hint` from `deeplinkHint`.** The location hint is read aloud („Hier:
            // {{label}} — {{hint}}"), but `deeplinkHint` is a note for maintainers and callers
            // about lost address parameters (CONTRACT.md §1) — „FALLSTRICK: ?tab wird beim
            // Reiterwechsel geschrieben, aber nie gelesen …" belongs in no announcement. Only the
            // tool's `highlight` block carries a short location hint (§7.3).
            requestSpotlight({
                guideId: feature.guideId,
                label: feature.name[locale] || feature.name.de,
            });
        })
        .catch(() => undefined);
}

/** The active language as a registry key; German if i18next is not initialized. */
function currentLocale(): 'de' | 'en' {
    return i18n.language?.startsWith('en') ? 'en' : 'de';
}

interface ParsedHref {
    pathname: string;
    search: string;
    hash: string;
}

/**
 * Backslash and ASCII control characters (TAB, LF, CR …): the WHATWG URL parser
 * treats `\` like `/` for http(s) and strips control characters, so `/\host`
 * or `/<TAB>/host` becomes `//host` — a foreign origin that looks like a portal
 * path before parsing.
 */
// eslint-disable-next-line no-control-regex -- exactly these control characters are the attack
const OFF_ORIGIN_CHARS = /[\\\u0000-\u001F\u007F]/;

/** Base for parsing relative addresses; any other host is a hit. */
const PARSE_BASE = 'http://portal.invalid';

/**
 * Accepts only portal-internal addresses: one leading slash, not a second one
 * (`//host` would be protocol-relative, i.e. external), no backslash/control
 * characters (the browser turns those into `//host`), in the end the same host
 * as the parse base, and no unfilled placeholders from the registry
 * (`{selectedEntity}`) — not even percent-encoded (`%7BselectedEntity%7D`).
 */
export function parseInternalHref(href: unknown): ParsedHref | null {
    if (typeof href !== 'string') return null;
    const trimmed = href.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
    if (OFF_ORIGIN_CHARS.test(trimmed)) return null;
    if (trimmed.includes('{') || trimmed.includes('}')) return null;

    let url: URL;
    try {
        url = new URL(trimmed, PARSE_BASE);
    } catch {
        return null;
    }

    // Belt and braces: whatever the parser pulled off-origin despite the character
    // check (current or future quirks) drops out here.
    if (url.origin !== PARSE_BASE) return null;

    /**
     * And the same `//` check once more against the **normalized** address.
     *
     * Above was the raw string; here is what react-router will get later — and
     * in between lies the URL parser's path normalization. Dot segments turn
     * `/.//attacker.example/login` into the `pathname`
     * `//attacker.example/login`: protocol-relative, i.e. a foreign origin.
     * `url.origin` notices nothing (the base stays `portal.invalid`), and
     * `createHref` passes a `pathname` starting with `/` through unchanged;
     * `pushState` then throws a `SecurityError`, which @remix-run/router catches
     * with `window.location.assign` — a full navigation to the foreign host,
     * with `auto: true` even without a click. `/..//host`, `/%2e//host` and
     * `/settings/..//host` set the same trap.
     */
    const serialized = `${url.pathname}${url.search}${url.hash}`;
    if (!serialized.startsWith('/') || serialized.startsWith('//')) return null;

    // Percent-encoded placeholders only become visible after decoding.
    let decoded: string;
    try {
        decoded = decodeURIComponent(serialized);
    } catch {
        return null; // broken percent sequence — unusable
    }
    if (/[{}]/.test(decoded)) return null;

    return { pathname: url.pathname, search: url.search, hash: url.hash };
}

/**
 * Addresses the navigator rejects for domain reasons even though they are
 * portal-internal: `/entity` without `selectedEntityType` — `EntityPage`
 * redirects to `/chatbot` immediately without a type, so a jump there is no
 * jump at all.
 *
 * Lives here rather than in the hook so the block readers draw the same
 * boundary as the click: otherwise a `navigate` block to
 * `/entity?selectedEntity=abc` would get a button that silently does nothing,
 * and an `auto` block would request the spotlight on the page the user stays on.
 */
function isNavigableTarget(target: ParsedHref): boolean {
    const path = foldPathname(target.pathname);
    if (path !== '/entity' && !path.startsWith('/entity/')) return true;
    return Boolean(new URLSearchParams(target.search).get('selectedEntityType'));
}

/**
 * The same decision as on click, for callers that only want to know whether the
 * navigator accepts the address at all (renderer: set href or not).
 */
// Deliberately `boolean` rather than a type predicate: `href is string` would narrow the
// renderer's fall-through branch to `never` (TS2339 in the build), although mailto:/tel: is still checked there.
export function isInternalGuideHref(href: unknown): boolean {
    return parseInternalHref(href) !== null;
}

/**
 * Stricter than `isInternalGuideHref`: portal-internal **and** acceptable to the
 * navigator (`isNavigableTarget`). Anyone who wants not just to render an
 * address but also to jump to it — `navigateHrefFrom`, `requestAutoNavigate` —
 * asks here.
 */
export function isNavigableGuideHref(href: unknown): boolean {
    const target = parseInternalHref(href);
    return target !== null && isNavigableTarget(target);
}

/** Folded path within the main chat (`/chatbot`, `/chatbot/…`). */
function isChatRoot(foldedPathname: string): boolean {
    return foldedPathname === CHAT_ROOT || foldedPathname.startsWith(`${CHAT_ROOT}/`);
}

function isQueryOnlyRoot(foldedPathname: string): boolean {
    return QUERY_ONLY_ROOTS.some(
        (root) => foldedPathname === root || foldedPathname.startsWith(`${root}/`),
    );
}

/**
 * Returns the jump function. Rules:
 * - same address (path + query, parameter order irrelevant) → `'same'`, no navigation;
 * - query-only change within `/settings` or `/administration` → `replace: true`;
 * - real route change → without `replace`, so "Back" leads to the chat;
 * - `/entity` without `selectedEntityType` → `'rejected'` (EntityPage would otherwise bounce to `/chatbot`);
 * - unfilled placeholder or external/protocol-relative address → `'rejected'`.
 *
 * Second argument `spotlightOnSame: false` for callers that, on `'same'`,
 * request their **own**, more precise spotlight (the runner in
 * `autoNavigate.ts` with the answer's `highlight` block). Without it, the
 * registry fallback from here would arrive one microtask later and overwrite
 * it — the store has no queue, the latest request wins.
 */
export function useGuideNavigate(): (
    href: string,
    options?: { spotlightOnSame?: boolean },
) => GuideNavigateResult {
    const navigate = useNavigate();
    const location = useLocation();

    return useCallback(
        (href: string, options?: { spotlightOnSame?: boolean }): GuideNavigateResult => {
            const target = parseInternalHref(href);
            if (!target) return 'rejected';
            // EntityPage redirects to /chatbot immediately without a type — better not to jump at all.
            if (!isNavigableTarget(target)) return 'rejected';

            const targetPath = foldPathname(target.pathname);
            const currentPath = foldPathname(location.pathname);
            if (currentPath === targetPath && foldSearch(location.search) === foldSearch(target.search)) {
                // Already there — and that is exactly the moment a link would
                // go nowhere: the user clicks „API-Schlüssel öffnen", the page
                // is already open, nothing happens, and they keep searching. If
                // the entry for this address carries a target, it is pointed at
                // instead (`spotlight.ts`) — unless the caller brings a more
                // precise one.
                if (options?.spotlightOnSame !== false) {
                    requestSpotlightForAddress(`${target.pathname}${target.search}`);
                }
                return 'same';
            }

            const to: { pathname: string; search: string; hash?: string } = {
                pathname: target.pathname,
                search: target.search,
            };
            if (target.hash) to.hash = target.hash;

            const queryOnly = currentPath === targetPath && isQueryOnlyRoot(targetPath);

            // Leaving the main chat: there the chat fills the whole shell, so
            // the jump would leave it. Instead of leaving the user on the
            // target page without a conversation, the conversation moves into
            // the question panel (see `chatHandoff.ts`). A jump *within*
            // `/chatbot` — different query, sub-path — is not leaving: there
            // the shell renders neither panel nor overlay, nobody would pick up
            // a requested handoff, and its morph clone would linger for the
            // whole deadline (and delay the grey „mitgekommen" line by
            // `whenMorphIdle`).
            //
            // Without `?conversationId` in the address (the conversation is not
            // in the URL yet) the handoff goes out without an id: the panel then
            // opens fresh, but it opens.
            if (isChatRoot(currentPath) && !isChatRoot(targetPath)) {
                const address = `${target.pathname}${target.search}`;
                const handoff = requestChatHandoff({
                    conversationId: new URLSearchParams(location.search).get('conversationId'),
                    target: address,
                });

                // And the visible part of the same movement: the chat surface
                // flies as its own shell to the panel's place
                // (`src/guide/morph/`). Measuring and cloning happen **here**,
                // in the click handler — at a moment when the surface is
                // guaranteed to be laid out and painted. After that, everything
                // the flight needs is a value; the overlay only measures its
                // target.
                //
                // The "morph or not" decision is **frozen** here (`decision`):
                // toggling `prefers-reduced-motion` or resizing the window
                // mid-flight must not switch the branch.
                //
                // Cost: two `getBoundingClientRect`, one `scrollTop` and one
                // `cloneNode(true)` before `navigate`. If no registered surface
                // is found or a rect is empty (jsdom), everything proceeds as
                // before the morph — the handoff is in place regardless.
                const environment = readMorphEnvironment();
                const morphs = shouldMorphNow(environment);
                const measurement = morphs ? measureAssistantSurface() : null;
                if (measurement) {
                    beginAssistantMorph({
                        handoffId: handoff.id,
                        target: address,
                        originRect: measurement.originRect,
                        surfaceRect: measurement.surfaceRect,
                        scrollTop: measurement.scrollTop,
                        fill: measurement.fill,
                        ghost: measurement.ghost,
                        snapshot: measurement.conversation,
                        decision: {
                            reducedMotion: environment.reducedMotion,
                            isMobile: environment.viewportWidth < MOBILE_BREAKPOINT,
                            morph: morphs,
                        },
                    });
                }
            }

            // Mark before the jump: the question panel may then skip its reset.
            markSelfNavigation(`${target.pathname}${target.search}`);
            if (queryOnly) {
                navigate(to, { replace: true });
            } else {
                navigate(to);
            }
            return 'navigated';
        },
        [navigate, location.pathname, location.search],
    );
}
